//src/pages/ImportarPixTikpag.jsx

import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import {
  BadgeDollarSign,
  CheckCircle2,
  Info,
  Save,
  Search,
  ShieldAlert,
  UserRound
} from "lucide-react";

import "./ImportarPixTikpag.css";

function ImportarPixTikpag() {
  const [usuarios, setUsuarios] = useState([]);
  const [clienteTexto, setClienteTexto] = useState("");
  const [usuarioId, setUsuarioId] = useState("");
  const [numeroPedido, setNumeroPedido] = useState("");
  const [pixChaveTipo, setPixChaveTipo] = useState("cpf");
  const [pixChaveValor, setPixChaveValor] = useState("");
  const [detalhes, setDetalhes] = useState(null);
  const [mensagem, setMensagem] = useState("");
  const [loading, setLoading] = useState(false);
  const [cadastrado, setCadastrado] = useState(false);

  useEffect(() => {
    axios
      .get("/clientes")
      .then((res) => setUsuarios(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMensagem("Erro ao carregar clientes."));
  }, []);

  useEffect(() => {
    const selecionado = usuarios.find(
      (u) => `${u.nome} (${u.email})`.toLowerCase() === clienteTexto.toLowerCase()
    );

    if (selecionado) {
      setUsuarioId(String(selecionado.id));
    }
  }, [clienteTexto, usuarios]);

  const usuarioSelecionado = useMemo(
    () => usuarios.find((u) => String(u.id) === String(usuarioId)),
    [usuarios, usuarioId]
  );

  const getMensagemErro = (err, fallback) => {
    return (
      err.response?.data?.mensagem ||
      err.response?.data?.error ||
      err.response?.data?.detalhe ||
      fallback
    );
  };

  const limparResultado = () => {
    setDetalhes(null);
    setCadastrado(false);
    setMensagem("");
  };

  const handlePreview = async (e) => {
    e.preventDefault();

    setMensagem("");
    setDetalhes(null);
    setCadastrado(false);

    if (!usuarioId || !numeroPedido || !pixChaveTipo || !pixChaveValor.trim()) {
      setMensagem("Selecione o cliente, informe o pedido e a chave Pix.");
      return;
    }

    setLoading(true);

    try {
      const resp = await axios.post("/pix/operacoes/preview", {
        usuario_id: Number(usuarioId),
        numero_pedido: numeroPedido,
        pix_chave_tipo: pixChaveTipo,
        pix_chave_valor: pixChaveValor.trim()
      });

      setDetalhes(resp.data?.resumo || null);
      setMensagem("Confira os dados antes de cadastrar a operação Pix.");
    } catch (err) {
      console.error("Erro ao gerar preview Pix:", err);
      setMensagem(
        getMensagemErro(
          err,
          "Não foi possível consultar este pedido para Pix. Confira os dados e tente novamente."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCadastrar = async () => {
    if (!detalhes) return;

    setMensagem("");
    setLoading(true);

    try {
      const resp = await axios.post("/pix/operacoes", {
        usuario_id: Number(usuarioId),
        numero_pedido: numeroPedido,
        pix_chave_tipo: pixChaveTipo,
        pix_chave_valor: pixChaveValor.trim()
      });

      setCadastrado(true);
      setMensagem(
        resp.data?.message ||
          "Operação Pix cadastrada com sucesso para o cliente."
      );
    } catch (err) {
      console.error("Erro ao cadastrar Pix pelo admin:", err);
      setMensagem(
        getMensagemErro(
          err,
          "Não foi possível cadastrar esta operação Pix. Tente novamente."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const formatarMoeda = (valor) => {
    return Number(valor || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL"
    });
  };

  const formatarData = (valor) => {
    if (!valor) return "-";
    try {
      return new Date(`${String(valor).slice(0, 10)}T00:00:00`).toLocaleDateString("pt-BR");
    } catch {
      return String(valor);
    }
  };

  return (
    <div className="importarPixTikpagAdmin-page">
      <div className="importarPixTikpagAdmin-topbar">
        <span className="importarPixTikpagAdmin-kicker">Pix Premiado</span>
        <h2>Cadastrar Pix para Cliente</h2>
        <p>
          Cadastre uma operação Pix em nome de um cliente. Esta rotina é administrativa
          e permite exceções de prazo sem alterar a regra aplicada ao cliente.
        </p>
      </div>

      <div className="importarPixTikpagAdmin-tabs">
        <a href="/admin/importar-operacao" className="importarPixTikpagAdmin-tab">
          Pontos
        </a>
        <span className="importarPixTikpagAdmin-tab active">
          Pix
        </span>
      </div>

      <div className="importarPixTikpagAdmin-alert warning">
        <ShieldAlert size={16} />
        Use esta rotina apenas para suporte operacional ou concessões autorizadas.
      </div>

      {mensagem && (
        <div className={`importarPixTikpagAdmin-alert ${cadastrado ? "success" : "info"}`}>
          {cadastrado ? <CheckCircle2 size={16} /> : <Info size={16} />}
          {mensagem}
        </div>
      )}

      <section className="importarPixTikpagAdmin-card">
        <div className="importarPixTikpagAdmin-cardHeader">
          <div className="importarPixTikpagAdmin-icon">
            <BadgeDollarSign size={20} />
          </div>

          <div>
            <h3>Dados para cadastro Pix</h3>
            <span>Selecione o cliente, informe o pedido e a chave Pix.</span>
          </div>
        </div>

        <form className="importarPixTikpagAdmin-form" onSubmit={handlePreview}>
          <div className="importarPixTikpagAdmin-field full">
            <label>Cliente</label>
            <div className="importarPixTikpagAdmin-inputIcon">
              <UserRound size={15} />
              <input
                type="text"
                placeholder="Digite nome ou e-mail do cliente"
                value={clienteTexto}
                onChange={(e) => {
                  setClienteTexto(e.target.value);
                  setUsuarioId("");
                  limparResultado();
                }}
                list="importarPixTikpag-clientes"
              />
            </div>

            <datalist id="importarPixTikpag-clientes">
              {usuarios
                .filter((u) =>
                  `${u.nome} ${u.email}`
                    .toLowerCase()
                    .includes(clienteTexto.toLowerCase())
                )
                .map((u) => (
                  <option key={u.id} value={`${u.nome} (${u.email})`} />
                ))}
            </datalist>
          </div>

          <div className="importarPixTikpagAdmin-field">
            <label>Número do pedido</label>
            <div className="importarPixTikpagAdmin-inputIcon">
              <Search size={15} />
              <input
                type="text"
                value={numeroPedido}
                onChange={(e) => {
                  setNumeroPedido(e.target.value.replace(/\D/g, ""));
                  limparResultado();
                }}
                placeholder="Ex: 123456"
                inputMode="numeric"
              />
            </div>
          </div>

          <div className="importarPixTikpagAdmin-field">
            <label>Tipo da chave Pix</label>
            <select
              value={pixChaveTipo}
              onChange={(e) => {
                setPixChaveTipo(e.target.value);
                limparResultado();
              }}
            >
              <option value="cpf">CPF</option>
              <option value="cnpj">CNPJ</option>
              <option value="email">E-mail</option>
              <option value="telefone">Telefone</option>
              <option value="aleatoria">Chave aleatória</option>
            </select>
          </div>

          <div className="importarPixTikpagAdmin-field full">
            <label>Chave Pix</label>
            <input
              type="text"
              value={pixChaveValor}
              onChange={(e) => {
                setPixChaveValor(e.target.value);
                limparResultado();
              }}
              placeholder="Informe a chave Pix do cliente"
            />
          </div>

          <div className="importarPixTikpagAdmin-clienteBox">
            <span>Cliente selecionado</span>
            <strong>{usuarioSelecionado?.nome || "Nenhum cliente selecionado"}</strong>
            <small>{usuarioSelecionado?.email || "Selecione pela busca acima"}</small>
          </div>

          <div className="importarPixTikpagAdmin-submitLine">
            <button
              type="submit"
              className="importarPixTikpagAdmin-btn primary"
              disabled={loading}
            >
              <Search size={16} />
              {loading ? "Consultando..." : "Consultar pedido"}
            </button>
          </div>
        </form>
      </section>

      {detalhes && (
        <section className="importarPixTikpagAdmin-card">
          <div className="importarPixTikpagAdmin-cardHeader">
            <div className="importarPixTikpagAdmin-icon teal">
              <CheckCircle2 size={20} />
            </div>

            <div>
              <h3>Resumo da operação Pix</h3>
              <span>Confira os dados antes de cadastrar para aprovação.</span>
            </div>
          </div>

          <div className="importarPixTikpagAdmin-resumo">
            <div className="importarPixTikpagAdmin-resumoGrid">
              <div className="importarPixTikpagAdmin-resumoItem">
                <span>Pedido</span>
                <strong>#{detalhes.numero_pedido}</strong>
              </div>

              <div className="importarPixTikpagAdmin-resumoItem">
                <span>Data da operação</span>
                <strong>{formatarData(detalhes.data_operacao)}</strong>
              </div>

              <div className="importarPixTikpagAdmin-resumoItem">
                <span>Pagador</span>
                <strong>{detalhes.nome_pagador || "-"}</strong>
              </div>

              <div className="importarPixTikpagAdmin-resumoItem">
                <span>Valor da operação</span>
                <strong>{formatarMoeda(detalhes.valor)}</strong>
              </div>

              <div className="importarPixTikpagAdmin-resumoItem">
                <span>Parcelas</span>
                <strong>{detalhes.numero_parcelas}x</strong>
              </div>

              <div className="importarPixTikpagAdmin-resumoItem destaque">
                <span>Valor Pix</span>
                <strong>{formatarMoeda(detalhes.pix_valor)}</strong>
              </div>

              <div className="importarPixTikpagAdmin-resumoItem">
                <span>Elegível</span>
                <strong>{detalhes.elegivel ? "Sim" : "Não"}</strong>
              </div>

              <div className="importarPixTikpagAdmin-resumoItem">
                <span>Tipo da chave</span>
                <strong>{detalhes.pix_chave_tipo || "-"}</strong>
              </div>

              <div className="importarPixTikpagAdmin-resumoItem">
                <span>Chave Pix</span>
                <strong>{detalhes.pix_chave_valor || "-"}</strong>
              </div>
            </div>

            <div className="importarPixTikpagAdmin-info">
              <Info size={16} />
              {detalhes.elegivel
                ? "Esta operação será cadastrada como pendente para aprovação do administrador."
                : "Esta operação será registrada como não elegível, mantendo o histórico para consulta."}
            </div>
          </div>

          <div className="importarPixTikpagAdmin-actions">
            <button
              type="button"
              className="importarPixTikpagAdmin-btn secondary"
              onClick={() => {
                setDetalhes(null);
                setCadastrado(false);
                setMensagem("");
              }}
              disabled={loading}
            >
              Limpar
            </button>

            <button
              type="button"
              className="importarPixTikpagAdmin-btn success"
              onClick={handleCadastrar}
              disabled={loading || cadastrado}
            >
              <Save size={16} />
              {loading ? "Salvando..." : cadastrado ? "Cadastrado" : "Cadastrar Pix"}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

export default ImportarPixTikpag;